import '../CreateEditCourseModal.scss';
import {i18n} from "../../../../Utils/i18n/i18n";
import {FormInput} from "../../../Form/Input/FormInput";
import React, {useEffect, useState} from "react";
import {Button} from "../../../Form/Button/Button";
import {buttonColors} from "../../../Pickers/InputTheme";
import {
    SA_editSignDoc,
    SA_getSignDoc,
    SA_uploadSignDocZipFileAPI,
} from "../../../../Services/SA_api";
import {FileUploadBox} from "../../../Smart/FileUploadBox/FileUploadBox";
import {cloneDeep, get, map} from "lodash";
import LoadingSpinner from "../../../LoadingSpinner/LoadingSpinner";
import {Modal} from "../../../Smart/Modal/Modal";
import {imageOptionsArray} from "../CreateEditCourseLib";
import {ImageSelectGallery} from "../ImageSelectGallery/ImageSelectGallery";


const imageOptions = cloneDeep(imageOptionsArray)

/**
 * Modal component for editing existing SignDoc courses in Super Admin interface.
 * Similar structure to CreateNewCourseModal but for editing existing content.
 *
 * @param {object} params - The component parameters
 * @param {string} params.initSignDocId - The ID of the SignDoc to edit
 * @param {boolean} params.editCourseModalOpen - Whether the modal is open
 * @param {function} params.closeEditCourseModal - Function to close the modal
 * @returns {JSX.Element} The edit SignDoc modal component
 */
export const SAEditSignDocModal = ({initSignDocId, editCourseModalOpen, closeEditCourseModal}) => {
    const [docName, setDocName] = useState()
    const [description, setDescription] = useState()
    const [signDocId, setSignDocId] = useState(initSignDocId)
    const [companyId, setCompanyId] = useState()
    const [isLoading, setIsLoading] = useState(false)
    const [selectedImageUrl, setSelectedImageUrl] = useState()
    const [companyName, setCompanyName] = useState()

    const editCourseName = (e) => {
        setDocName(e.target.value)
    }

    const editCourseDescription = (e) => {
        setDescription(e.target.value)
    }

    /**
     * Initializes the component by fetching SignDoc data and companies
     */
    const init = async () => {
        setIsLoading(true)
        const signDoc = await SA_getSignDoc({signDocId})
        setCompanyId(get(signDoc, 'company.id'))
        setCompanyName(get(signDoc, 'company.companyName'))
        //if imgSrc not in imageOptions, add it to the list
        if (!imageOptions.includes(get(signDoc, 'imgSrc'))) {
            imageOptions.push(get(signDoc, 'imgSrc'))
        }
        setSelectedImageUrl(get(signDoc, 'imgSrc'))
        setSignDocId(signDoc.id)
        setDocName(signDoc.docName)
        setDescription(signDoc.description)
        setIsLoading(false)
    }

    useEffect(() => {
        init()
    }, []);


    const uploadCourseZip = async ({files, onProgress}) => {
        return SA_uploadSignDocZipFileAPI({files, onProgress, signDocId})
    }

    /**
     * Handles image URL selection
     * @param {string} imageUrl - Selected image URL
     * @returns {function} Event handler function
     */
    const onSelectedImageUrl = (imageUrl) => () => {
        setSelectedImageUrl(imageUrl)
    }

    /**
     * Saves the edited course data
     */
    const onSaveEditedCourse = async () => {
        setIsLoading(true)
        const newCourseData = await SA_editSignDoc({signDocId, docName, description, imgSrc: selectedImageUrl})
        setSelectedImageUrl(get(newCourseData, 'imgSrc'))
        setSignDocId(newCourseData.id)
        setDocName(newCourseData.docName)
        setDescription(newCourseData.description)
        setIsLoading(false)
        closeEditCourseModal()
    }

    return <Modal
        open={editCourseModalOpen}
        onClose={closeEditCourseModal}>
        <div className="CreateEditCourse modalWrapper">
            <div className="modalTitle">
                {i18n.translate('superAdmin.editCourse.modalTitle')}
            </div>
            <div className="modalContent">
                {isLoading ? <LoadingSpinner/> : ''}
                <div className="upperRow">
                    <div className="courseDetails">
                        <FormInput
                            label={i18n.translate('superAdmin.editCourse.docName')}
                            className=""
                            value={docName}
                            onChange={editCourseName}
                        />
                        <FormInput
                            label={i18n.translate('superAdmin.editCourse.description')}
                            className=""
                            value={description}
                            onChange={editCourseDescription}
                        />
                        <FormInput
                            label={i18n.translate('superAdmin.editCourse.companyName')}
                            className=""
                            value={companyName}
                            disabled={true}
                        />
                    </div>

                    {signDocId ? <div className={`courseZipFile`}>
                        <FileUploadBox uploadFiles={uploadCourseZip} maxSize={600 * 1024 * 1024}>
                            {/*600MB*/}
                            <div className="text1">{i18n.translate('superAdmin.editCourse.dropZipHere')}</div>
                            <div className="text2">{i18n.translate('superAdmin.editCourse.dropZipNote')}</div>
                        </FileUploadBox>
                    </div> : ''}
                </div>
                <ImageSelectGallery selectedImageUrl={selectedImageUrl} onSelectedImageUrl={onSelectedImageUrl} imageOptions={imageOptions}/>
                <div className="saveContainer">
                    <Button
                        color={buttonColors.GREY}
                        onClick={onSaveEditedCourse}
                        disabled={docName === '' || !docName || description === '' || !description}
                        text={i18n.translate('general.saveChanges')}
                    />
                </div>
            </div>
            <div className="modalFooter">
                <Button
                    color={buttonColors.YELLOW}
                    onClick={closeEditCourseModal}
                    text={i18n.translate('general.close')}
                />
            </div>
        </div>
    </Modal>
}